import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import usePromoStatus from "@/lib/usePromoStatus";

/**
 * Resume-to-checkout banner.
 *
 * Shown only to free-tier users who arrived at the dashboard with a pending
 * tier intent — either as a `?intent=basic|premium` query param (fresh from
 * the Onboarding Quiz they entered from a Pricing card) or a persisted
 * `summit_intent_tier` localStorage flag (returning session).
 *
 * The banner is deliberately loud: it's the single "one click to pay" bridge
 * between the marketing site and checkout, and closes the visitor→customer
 * loop that the raw /pricing → /checkout flow used to break.
 */
export default function PendingUpgradeBanner({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { params: P } = usePromoStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);

  // Resolve the intent: URL param wins (freshly minted), else localStorage.
  const queryIntent = searchParams.get("intent");
  const [intent, setIntent] = useState(() => {
    if (queryIntent === "basic" || queryIntent === "premium") return queryIntent;
    try {
      const stored = localStorage.getItem("summit_intent_tier");
      if (stored === "basic" || stored === "premium") return stored;
    } catch { /* ignore */ }
    return null;
  });

  // Fire a one-shot analytics event when the banner actually renders for a
  // free user — helps us measure the "quiz → dashboard nudge → checkout"
  // funnel independent of the direct /pricing → /checkout path.
  useEffect(() => {
    if (!intent || !user || user.tier !== "free") return;
    api.post("/analytics/pricing-view", {
      source: `dashboard-resume-${intent}`,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    }).catch(() => { /* best-effort */ });
    // Persist so refreshes keep it visible until the user acts.
    try { localStorage.setItem("summit_intent_tier", intent); } catch { /* ignore */ }
  }, [intent, user]);

  if (!user || user.tier !== "free" || !intent || dismissed) return null;

  const label = intent === "basic" ? t("tier.basic") : t("tier.premium");
  const price = intent === "basic" ? P.basic : P.premium;
  const blurb = intent === "basic"
    ? t("pendingUpgrade.blurbBasic")
    : t("pendingUpgrade.blurbPremium");

  const goToCheckout = () => {
    // Keep the intent stored — the checkout flow clears it on success.
    navigate(`/checkout/agreement/${intent}`);
  };

  const clearIntent = () => {
    try { localStorage.removeItem("summit_intent_tier"); } catch { /* ignore */ }
    // Strip ?intent= so a page refresh doesn't re-hydrate it.
    if (searchParams.has("intent")) {
      const next = new URLSearchParams(searchParams);
      next.delete("intent");
      setSearchParams(next, { replace: true });
    }
    setDismissed(true);
  };

  return (
    <section
      data-testid="dashboard-pending-upgrade-banner"
      className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white"
    >
      <div className="absolute -top-20 -right-24 w-80 h-80 rounded-full bg-amber-500/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />

      <button
        type="button"
        onClick={clearIntent}
        aria-label={t("pendingUpgrade.dismiss")}
        data-testid="dashboard-pending-upgrade-dismiss"
        className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
      >
        <X size={16} />
      </button>

      <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
        <div className="lg:col-span-2">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300">
            <Sparkles size={11} /> {t("pendingUpgrade.eyebrow", { tier: label })}
          </div>
          <h2 className="mt-4 font-display font-black text-2xl sm:text-3xl leading-[1.1] tracking-tighter">
            {t("pendingUpgrade.headline", { tier: label })}
          </h2>
          <p className="mt-3 text-sm text-slate-300 max-w-xl leading-relaxed">
            {blurb}
          </p>
        </div>

        <div className="lg:pl-6 lg:border-l lg:border-white/10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 font-semibold">
            {t("pendingUpgrade.oneTime")}
          </div>
          <div className="mt-1 font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
            {price}
          </div>
          <button
            type="button"
            onClick={goToCheckout}
            data-testid="dashboard-pending-upgrade-cta"
            className="mt-5 inline-flex items-center gap-2 w-full justify-center text-sm font-semibold px-5 py-3 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 transition-colors"
          >
            {t("pendingUpgrade.continueCheckout")} <ArrowRight size={14} />
          </button>
          <button
            type="button"
            onClick={clearIntent}
            data-testid="dashboard-pending-upgrade-notyet"
            className="mt-2 w-full text-xs text-slate-400 hover:text-white transition-colors py-1"
          >
            {t("pendingUpgrade.notYet")}
          </button>
        </div>
      </div>
    </section>
  );
}
